/*
 * RapidFirePractice — "AI Rapid Fire", a Practice mode (#/practice/rapid-fire).
 * Can I recall and apply this quickly? A light setup, then short questions one
 * after another, each graded in a line, then a compact summary.
 *
 * Question sources (all existing engine endpoints):
 *   quick recall   /engine/rapidfire     one-liners with a reference answer
 *   concept        /engine/theory/next   honours difficulty (easy / medium / hard)
 *   follow-up      /engine/theory/drill  a related question on what was just missed
 * Grading: /engine/blitz/grade (one short verdict per answer). Each answer and
 * the round are logged to learning memory through memoryLog's rapidfire path —
 * the same path the spoken rapid-fire round uses — so Progress, Learning Memory
 * and Practice recommendations pick them up.
 *
 * Adaptive difficulty: a correct answer pitches the next concept question a
 * level higher, a miss a level lower and queues a follow-up on the gap.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-practice.css';
import '../../styles/prepare-up-hub.css';       // kh-select
import '../../styles/prepare-up-progress.css';  // pg-seg
import '../../styles/prepare-up-rapidfire.css';
import APIService from '../../services/APIService';
import PracticeChooser from './PracticeChooser';
import { HomeSearch, ThemeToggle, buildSearchEntries } from './HomeDashboard';
import { buildPaths, getHubTreeCached, indexTree } from './paths';
import { logRapidfireQuestion, logRapidfireRound } from './memoryLog';
import { IconArrowLeft, IconArrowRight, IconBolt, IconCheck, IconMic, IconStop, IconX } from './icons';

const COUNTS = [5, 10, 20];
const DIFFS = [[null, 'Adaptive'], ['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard']];
const FOCUS = [
  ['mixed', 'Mixed', 'Quick recall and concept questions, alternating'],
  ['recall', 'Recall', 'One-line facts and definitions'],
  ['concepts', 'Concepts', 'Explain the idea behind it'],
  ['application', 'Application', 'Use it — pitched a level harder'],
  ['interview', 'Interview knowledge', 'One-liners plus short interview-style prompts'],
];
const LEVELS = ['easy', 'medium', 'hard'];
const KIND_LABEL = { recall: 'Quick recall', behavioral: 'Interview prompt', concept: 'Concept', application: 'Application', followup: 'Follow-up' };
const WEAK_KEY = '__weak__';
const PREFILL_KEY = '__prefill__';   // several topics handed over at once (e.g. the goal)

const verdictOf = (g) => (g.correct ? 'correct' : g.score >= 40 ? 'review' : 'wrong');
const VERDICT = { correct: 'Correct', review: 'Needs review', wrong: 'Incorrect' };

export default function RapidFirePractice({
  initialSubjects, profile, onEditProfile, theme, onToggleTheme, onOpenPath, onOpenHubNode, onBack, onStartPractice,
}) {
  const [hub, setHub] = useState(null);
  const [mem, setMem] = useState(null);
  useEffect(() => {
    getHubTreeCached().then((d) => setHub(d.tree || [])).catch(() => setHub([]));
    APIService.getMemoryTree().then((d) => setMem(d.tree || [])).catch(() => setMem([]));
  }, []);
  const byId = useMemo(() => indexTree(hub || []), [hub]);
  const memIdx = useMemo(() => indexTree(mem || []), [mem]);
  const paths = useMemo(() => (hub ? buildPaths(hub) : null), [hub]);
  const searchEntries = useMemo(() => buildSearchEntries(hub, paths ? [...paths.roles, ...paths.areas] : []), [hub, paths]);
  const topicName = useCallback((cid) => {
    if (!cid) return '';
    const parts = String(cid).split('.');
    for (let i = Math.min(2, parts.length); i > 0; i -= 1) { const n = byId[parts.slice(0, i).join('.')]; if (n) return n.name; }
    return parts[parts.length - 1].replace(/_/g, ' ');
  }, [byId]);
  const topicId = (cid) => { const parts = String(cid || '').split('.'); return byId[parts.slice(0, 2).join('.')] ? parts.slice(0, 2).join('.') : parts[0]; };

  // Weak areas: practised topics, weakest first (needs a few attempts to count).
  const weakAreas = useMemo(() => (mem || []).flatMap((r) => (r.children || []).map((c) => ({ ...c, root: r })))
    .filter((t) => t.attempts > 0)
    .sort((a, b) => ((b.status === 'weak') - (a.status === 'weak')) || (a.mastery || 0) - (b.mastery || 0))
    .slice(0, 3), [mem]);

  /* ── setup ── */
  const prefill = initialSubjects && initialSubjects.length ? initialSubjects : null;
  const [topic, setTopic] = useState(prefill ? (prefill.length > 1 ? PREFILL_KEY : prefill[0].id) : WEAK_KEY);
  const [difficulty, setDifficulty] = useState(null);
  const [count, setCount] = useState(10);
  const [focus, setFocus] = useState('mixed');
  const [phase, setPhase] = useState('setup');   // setup | loading | play | done
  const [err, setErr] = useState('');

  const scope = useMemo(() => {
    if (topic === WEAK_KEY) return weakAreas.length ? weakAreas.map((t) => t.id) : (hub || []).filter((r) => r.id !== 'dsa').map((r) => r.id);
    if (topic === PREFILL_KEY) return prefill.map((x) => x.id);
    return [topic];
  }, [topic, weakAreas, hub, prefill]);
  const scopeLabel = topic === WEAK_KEY
    ? (weakAreas.length ? weakAreas.map((t) => t.name).join(', ') : 'Your topics')
    : topic === PREFILL_KEY ? prefill.map((x) => x.name).join(', ')
    : (byId[topic]?.name || prefill?.find((s) => s.id === topic)?.name || topic);

  /* ── session state ── */
  const [items, setItems] = useState([]);        // [{ q, kind, answer, grade, verdict }]
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [grading, setGrading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const [level, setLevel] = useState('medium');
  const [trend, setTrend] = useState(0);          // +1 harder, −1 easier, 0 steady (last change)
  const [learnMore, setLearnMore] = useState(false);
  const [chooser, setChooser] = useState(null);
  const poolRef = useRef([]);
  const recentRef = useRef([]);
  const drillRef = useRef(null);                  // pending follow-up { question, gap, concept_ids }
  const nextRef = useRef(null);                   // in-flight next question
  const t0 = useRef(0);
  const inputRef = useRef(null);

  const levelFor = useCallback((lv) => {
    const base = difficulty || lv;
    if (focus !== 'application') return base;
    return LEVELS[Math.min(2, LEVELS.indexOf(base) + 1)];
  }, [difficulty, focus]);

  // Which source serves question n.
  const sourceFor = (n) => {
    if (focus === 'recall' || focus === 'interview') return 'pool';
    if (focus === 'concepts' || focus === 'application') return 'theory';
    return n % 2 === 0 ? 'pool' : 'theory';
  };

  const fetchQuestion = useCallback(async (n, lv) => {
    if (drillRef.current) {
      const d = drillRef.current; drillRef.current = null;
      try {
        const q = await APIService.engineTheoryDrill({ question: d.question, gap: d.gap, conceptIds: d.concept_ids });
        if (q && q.question) return { q: { ...q, concept_ids: q.concept_ids?.length ? q.concept_ids : d.concept_ids }, kind: 'followup' };
      } catch { /* fall through to a regular question */ }
    }
    const src = sourceFor(n);
    if (src === 'pool' && poolRef.current.length) {
      const q = poolRef.current.shift();
      return { q, kind: q.kind === 'behavioral' ? 'behavioral' : 'recall' };
    }
    const q = await APIService.engineTheoryNext({ conceptIds: scope, recent: recentRef.current.slice(-10), difficulty: levelFor(lv) });
    if (!q || !q.question) throw new Error('No question came back for this topic.');
    return { q, kind: focus === 'application' ? 'application' : 'concept', level: levelFor(lv) };
  }, [scope, focus, levelFor]);   // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setErr(''); setPhase('loading');
    setItems([]); setIdx(0); setAnswer(''); setLearnMore(false); setTrend(0);
    setLevel('medium'); drillRef.current = null; nextRef.current = null; recentRef.current = [];
    try {
      poolRef.current = [];
      if (focus !== 'concepts' && focus !== 'application') {
        const res = await APIService.engineRapidfire({ conceptIds: scope, count: count + 2 });
        const qs = (res?.questions || []).filter((q) => q.question && (focus === 'interview' || q.kind !== 'behavioral'))
          .map((q) => ({ ...q, answer: q.answer || q.reference_answer || '' }));   // one-liners carry reference_answer
        poolRef.current = qs;
      }
      const first = await fetchQuestion(0, 'medium');
      recentRef.current.push(first.q.question);
      setItems([{ ...first, answer: '', grade: null }]);
      t0.current = Date.now();
      setPhase('play');
    } catch (e) {
      setErr(e.message || 'Could not prepare the round.');
      setPhase('setup');
    }
  };

  const cur = items[idx];
  useEffect(() => { if (phase === 'play' && cur && !cur.grade) inputRef.current?.focus(); }, [phase, idx, cur]);

  const submit = async () => {
    if (!cur || cur.grade || grading || !answer.trim()) return;
    setGrading(true); setErr('');
    try {
      const res = await APIService.engineBlitzGrade({ items: [{
        question: cur.q.question, reference_answer: cur.q.answer || '', answer,
        kind: cur.kind === 'behavioral' ? 'behavioral' : 'technical', concept_ids: cur.q.concept_ids || [],
      }] });
      const g = (res?.results || [])[0] || { score: 0, correct: false, feedback: '' };
      const verdict = verdictOf(g);
      logRapidfireQuestion(cur.q.concept_ids || [], g.correct ? 'correct' : 'wrong');
      setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, answer, grade: g, verdict } : it)));
      // adapt
      if (!difficulty) {
        const li = LEVELS.indexOf(level);
        const nl = g.correct ? LEVELS[Math.min(2, li + 1)] : LEVELS[Math.max(0, li - 1)];
        setTrend(nl === level ? 0 : g.correct ? 1 : -1);
        setLevel(nl);
        if (!g.correct && cur.kind !== 'followup' && cur.kind !== 'behavioral' && idx + 1 < count) {
          drillRef.current = { question: cur.q.question, gap: g.feedback || 'the core idea', concept_ids: cur.q.concept_ids || scope };
        }
        if (idx + 1 < count) nextRef.current = fetchQuestion(idx + 1, nl);
      } else if (idx + 1 < count) {
        nextRef.current = fetchQuestion(idx + 1, level);
      }
    } catch (e) {
      setErr(e.message || 'Grading failed — try again.');
    } finally {
      setGrading(false);
    }
  };

  const finish = useCallback((all) => {
    const graded = all.filter((it) => it.grade && it.verdict !== 'skipped');
    const counts = { correct: graded.filter((it) => it.grade.correct).length, wrong: graded.filter((it) => !it.grade.correct).length, too_slow: 0, skipped: all.length - graded.length };
    const avg = graded.length ? graded.reduce((s, it) => s + it.grade.score, 0) / graded.length : 0;
    // a round with no submitted answers says nothing about memory — don't log it
    if (all.some((it) => it.grade && it.verdict !== 'skipped')) {
      logRapidfireRound({ overall_score: avg, metrics: { rapidfire_counts: counts }, topics: [scopeLabel] },
        [...new Set(all.flatMap((it) => it.q.concept_ids || []))]);
    }
    setPhase('done');
  }, [scopeLabel]);

  const next = async () => {
    if (!cur) return;
    setLearnMore(false); setAnswer('');
    if (idx + 1 >= count) { finish(items); return; }
    setNextLoading(true); setErr('');
    try {
      const nq = await (nextRef.current || fetchQuestion(idx + 1, level));
      nextRef.current = null;
      recentRef.current.push(nq.q.question);
      setItems((prev) => [...prev, { ...nq, answer: '', grade: null }]);
      setIdx(idx + 1);
    } catch (e) {
      nextRef.current = null;
      setErr(e.message || 'Could not load the next question.');
    } finally {
      setNextLoading(false);
    }
  };
  const skip = () => {
    if (!cur || cur.grade) return;
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, grade: { score: 0, correct: false, feedback: 'Skipped.' }, verdict: 'skipped' } : it)));
    if (idx + 1 < count) nextRef.current = fetchQuestion(idx + 1, level);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (cur?.grade) next(); else submit(); }
  };
  useEffect(() => {
    const h = (e) => { if (e.key === 'Enter' && phase === 'play' && cur?.grade && !nextLoading && document.activeElement?.tagName !== 'TEXTAREA') next(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  /* ── voice answer (existing transcription endpoint) ── */
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const toggleMic = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const b64 = await new Promise((res) => { const r = new FileReader(); r.onloadend = () => res(String(r.result).split(',')[1]); r.readAsDataURL(blob); });
          const t = await APIService.transcribeAudio(b64);
          if (t && t.transcription) setAnswer((a) => (a ? `${a} ` : '') + t.transcription);
        } catch { setErr('Could not transcribe that — try typing instead.'); } finally { setTranscribing(false); }
      };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch {
      setErr('Microphone unavailable — type your answer instead.');
    }
  };

  /* ── summary ── */
  const summary = useMemo(() => {
    if (phase !== 'done') return null;
    const graded = items.filter((it) => it.grade && it.verdict !== 'skipped');
    const correct = items.filter((it) => it.grade?.correct).length;
    const byTopic = {};
    graded.forEach((it) => {          // skipped questions say nothing about a topic
      const cid = (it.q.concept_ids || [])[0];
      const k = cid ? topicId(cid) : 'general';
      byTopic[k] = byTopic[k] || { id: k, name: cid ? topicName(cid) : 'General', n: 0, right: 0, misses: 0 };
      byTopic[k].n += 1;
      if (it.grade?.correct) byTopic[k].right += 1; else byTopic[k].misses += 1;
    });
    const topics = Object.values(byTopic);
    return {
      correct, total: items.length, answered: graded.length, skipped: items.length - graded.length,
      accuracy: items.length ? correct / items.length : 0,
      strong: topics.filter((t) => t.right / t.n >= 0.7).sort((a, b) => b.right / b.n - a.right / a.n),
      weak: topics.filter((t) => t.right / t.n < 0.7).sort((a, b) => a.right / a.n - b.right / b.n),
      minutes: Math.max(1, Math.round((Date.now() - t0.current) / 60000)),
    };
  }, [phase, items]);   // eslint-disable-line react-hooks/exhaustive-deps

  const practise = (t) => {
    const node = byId[t.id];
    if (!node) return;
    setChooser({ title: node.name, subjects: [{ id: node.id, name: node.name, kind: 'topic' }] });
  };
  const learnNode = cur ? byId[topicId((cur.q.concept_ids || [])[0])] : null;
  const displayName = profile?.display_name || profile?.name || '';
  const diffText = difficulty ? `${DIFFS.find(([v]) => v === difficulty)[1]} difficulty` : 'Adaptive difficulty';

  return (
    <div className="pu-home pu-practice pu-rapid">
      <div className="pu-page">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries} onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile} aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>

        <section className="rpf-head">
          <button type="button" className="pu-link" onClick={onBack}><IconArrowLeft size={13} /> Back to Practice</button>
          <h1><IconBolt size={22} /> AI Rapid Fire</h1>
          {phase === 'setup' || phase === 'loading'
            ? <p>Test how quickly you can recall and apply what you know.</p>
            : <p className="rpf-meta"><span>{scopeLabel}</span><span>{count} questions</span><span>{diffText}</span></p>}
        </section>

        {(phase === 'setup' || phase === 'loading') && (
          <section className="pu-panel rpf-setup" aria-labelledby="rpf-setup-h">
            <h2 id="rpf-setup-h" className="pu-sr">Set up your round</h2>
            <label className="rpf-field">
              <span>Topic or skill</span>
              <span className="kh-select rpf-select">
                <select value={topic} onChange={(e) => setTopic(e.target.value)} aria-label="Topic or skill">
                  <option value={WEAK_KEY}>Based on your weak areas{weakAreas.length ? ` — ${weakAreas.map((t) => t.name).join(', ')}` : ''}</option>
                  {prefill && prefill.length > 1 && <option value={PREFILL_KEY}>{prefill.map((x) => x.name).join(', ')}</option>}
                  {prefill && prefill.length === 1 && !byId[prefill[0].id] && <option value={prefill[0].id}>{prefill[0].name}</option>}
                  {(hub || []).map((r) => (
                    <optgroup key={r.id} label={r.name}>
                      <option value={r.id}>All of {r.name}</option>
                      {(r.children || []).map((c) => <option key={c.id} value={c.id}>{c.name}{memIdx[c.id]?.status === 'weak' ? ' · weak' : ''}</option>)}
                    </optgroup>
                  ))}
                </select>
              </span>
            </label>
            <div className="rpf-row">
              <div className="rpf-field">
                <span>Difficulty</span>
                <div className="pg-seg" role="group" aria-label="Difficulty">
                  {DIFFS.map(([v, l]) => <button key={l} type="button" className={difficulty === v ? 'on' : ''} aria-pressed={difficulty === v} onClick={() => setDifficulty(v)}>{l}</button>)}
                </div>
              </div>
              <div className="rpf-field">
                <span>Questions</span>
                <div className="pg-seg" role="group" aria-label="Number of questions">
                  {COUNTS.map((n) => <button key={n} type="button" className={count === n ? 'on' : ''} aria-pressed={count === n} onClick={() => setCount(n)}>{n}</button>)}
                </div>
              </div>
            </div>
            <div className="rpf-field">
              <span>Focus</span>
              <div className="rpf-focus" role="group" aria-label="Focus">
                {FOCUS.map(([id, l, note]) => (
                  <button key={id} type="button" className={focus === id ? 'on' : ''} aria-pressed={focus === id} onClick={() => setFocus(id)}>
                    <b>{l}</b><small>{note}</small>
                  </button>
                ))}
              </div>
            </div>
            {err && <p className="pu-empty">{err}</p>}
            <div className="rpf-setup-foot">
              <span className="pu-meta">{difficulty ? '' : 'Adaptive: a correct answer makes the next question a little harder; a miss brings a follow-up on what you missed.'}</span>
              <button type="button" className="pu-btn pu-btn-primary" disabled={phase === 'loading' || !hub} onClick={start}>
                {phase === 'loading' ? 'Preparing your questions…' : <>Start Rapid Fire <IconArrowRight size={14} /></>}
              </button>
            </div>
          </section>
        )}

        {phase === 'play' && cur && (
          <section className="pu-panel rpf-play" aria-live="polite">
            <div className="rpf-progress">
              <span>Question {idx + 1} of {count}</span>
              <span className="rpf-dots" aria-hidden="true">
                {Array.from({ length: count }, (_, i) => {
                  const it = items[i];
                  return <i key={i} className={it?.verdict ? `v-${it.verdict}` : i === idx ? 'cur' : ''} />;
                })}
              </span>
              <span className="rpf-adapt" title="The next concept question adapts to your answers">
                {difficulty ? diffText : <>Adaptive · {level}{trend > 0 ? ' ↑' : trend < 0 ? ' ↓' : ''}</>}
              </span>
            </div>

            <div className="rpf-q">
              <span className="rpf-kind">{KIND_LABEL[cur.kind]}{(cur.q.concept_ids || [])[0] ? ` · ${topicName(cur.q.concept_ids[0])}` : ''}</span>
              <h2>{cur.q.question}</h2>
            </div>

            <div className="rpf-answer">
              <textarea ref={inputRef} rows={2} value={cur.grade ? cur.answer : answer} readOnly={!!cur.grade}
                onChange={(e) => setAnswer(e.target.value)} onKeyDown={onKey}
                placeholder={transcribing ? 'Transcribing…' : 'Type a short answer — Enter to submit'} aria-label="Your answer" />
              {!cur.grade && (
                <button type="button" className={`rpf-mic${recording ? ' on' : ''}`} onClick={toggleMic} disabled={transcribing}
                  aria-label={recording ? 'Stop recording' : 'Answer by voice'} title={recording ? 'Stop recording' : 'Answer by voice'}>
                  {recording ? <IconStop size={16} /> : <IconMic size={16} />}
                </button>
              )}
            </div>

            {cur.grade ? (
              <div className={`rpf-verdict v-${cur.verdict}`}>
                <b>{cur.verdict === 'correct' ? <IconCheck size={14} /> : cur.verdict === 'skipped' ? null : <IconX size={13} />}{cur.verdict === 'skipped' ? 'Skipped' : VERDICT[cur.verdict]}</b>
                {cur.grade.feedback && cur.verdict !== 'skipped' && <span>{cur.grade.feedback}</span>}
                {(cur.q.answer || learnNode) && (
                  <button type="button" className="pu-link" onClick={() => setLearnMore((v) => !v)} aria-expanded={learnMore}>Learn more <IconArrowRight size={12} /></button>
                )}
                {learnMore && (
                  <div className="rpf-more">
                    {cur.q.answer && <p><strong>A strong answer:</strong> {cur.q.answer}</p>}
                    {learnNode && <button type="button" className="pu-link" onClick={() => onOpenHubNode?.(learnNode.id)}>Read {learnNode.name} in the Knowledge Hub</button>}
                  </div>
                )}
              </div>
            ) : null}
            {err && <p className="pu-empty">{err}</p>}

            <div className="rpf-actions">
              {!cur.grade ? (
                <>
                  <button type="button" className="pu-btn pu-btn-quiet" onClick={skip} disabled={grading}>Skip</button>
                  <button type="button" className="pu-btn pu-btn-primary" onClick={submit} disabled={!answer.trim() || grading}>
                    {grading ? 'Checking…' : <>Submit Answer <IconArrowRight size={14} /></>}
                  </button>
                </>
              ) : (
                <button type="button" className="pu-btn pu-btn-primary" onClick={next} disabled={nextLoading}>
                  {nextLoading ? 'Loading…' : idx + 1 >= count ? <>See results <IconArrowRight size={14} /></> : <>Next <IconArrowRight size={14} /></>}
                </button>
              )}
            </div>
            <button type="button" className="pu-link rpf-end" onClick={() => finish(items)}>End round</button>
          </section>
        )}

        {phase === 'done' && summary && (
          <section className="pu-panel rpf-done" aria-labelledby="rpf-done-h">
            <h2 id="rpf-done-h">Rapid Fire Complete</h2>
            <div className="rpf-score">
              <div><b>{summary.correct} / {summary.total}</b><span>correct</span></div>
              <div><b>{Math.round(summary.accuracy * 100)}%</b><span>accuracy</span></div>
              <div><b>{summary.minutes} min</b><span>time</span></div>
              {summary.skipped > 0 && <div><b>{summary.skipped}</b><span>skipped</span></div>}
            </div>
            <div className="rpf-topics">
              <div>
                <h3>Strong</h3>
                {summary.strong.length ? <ul>{summary.strong.map((t) => <li key={t.id}><b>{t.name}</b><small>{t.right}/{t.n} right</small></li>)}</ul>
                  : <p className="pu-meta">Nothing above 70% this round.</p>}
              </div>
              <div>
                <h3>Needs practice</h3>
                {summary.weak.length ? (
                  <ul>
                    {summary.weak.map((t) => (
                      <li key={t.id}>
                        <b>{t.name}</b>
                        <small>{t.right}/{t.n} right{t.misses >= 2 ? ' · Needs reinforcement' : ''}</small>
                        {byId[t.id] && <button type="button" className="pu-link" onClick={() => practise(t)}>Practice this <IconArrowRight size={12} /></button>}
                      </li>
                    ))}
                  </ul>
                ) : <p className="pu-meta">No weak spots this round.</p>}
              </div>
            </div>
            <p className="pu-meta">{summary.answered
              ? 'Your answers are saved to your Learning Memory and feed Progress and your recommendations.'
              : 'No answers were submitted this round, so nothing was saved to your Learning Memory.'}</p>
            <div className="rpf-done-acts">
              {summary.weak[0] && byId[summary.weak[0].id] && (
                <button type="button" className="pu-btn pu-btn-primary" onClick={() => practise(summary.weak[0])}>Practice Weak Area <IconArrowRight size={14} /></button>
              )}
              <button type="button" className="pu-btn pu-btn-quiet" onClick={start}>Try Again</button>
              <button type="button" className="pu-btn pu-btn-quiet" onClick={onBack}>Back to Practice</button>
            </div>
          </section>
        )}
      </div>
      {chooser && (
        <PracticeChooser title={chooser.title} subjects={chooser.subjects}
          onStart={(o) => { setChooser(null); onStartPractice?.(o); }} onClose={() => setChooser(null)} />
      )}
    </div>
  );
}
